import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getApiRoute } from "src/services";
import {
  getRedirectResult,
  connectAuthEmulator,
  isSignInWithEmailLink,
  signInWithEmailLink,
  checkActionCode,
} from "firebase/auth";
import { auth } from "../firebase/firebase.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { customEvent } from "./gtag.js";
import { capitalize } from "lodash";
import { rollbar } from "./ErrorBoundary/index.js";
import { isEmbeddedPath } from "./Utils.js";
import {
  emailLinkRedeemErrorMessage,
  firebaseAuthErrorMessage,
  isAxiosRequestAbort,
  resolveBackendError,
  SESSION_NOT_LOADED_MESSAGE,
} from "./authErrorUtils.js";
import {
  RELAY_REDIRECT_KEY,
  SIGN_IN_REDIRECT_IN_PROGRESS_KEY,
  SIGN_IN_REDIRECT_PROVIDER_KEY,
} from "./firebaseSignIn.js";
import {
  emailDomainOf,
  isSignInCancel,
  reportSignInError,
  trackProviderSignInFailure,
} from "./signInReporting.js";

const GET_AUTH_ERROR_SHAPES = {
  INVALID_SESSION: {
    message: "Your session is invalid or expired. Please sign in again.",
    isExpectedAuthFailure: true,
  },
  USER_NOT_FOUND: {
    message: "We couldn't find your account. Please sign in again.",
    isExpectedAuthFailure: true,
  },
};

const GET_AUTH_ERROR_CODES_BY_MESSAGE = {
  Unauthorized: "INVALID_SESSION",
  "User not found": "USER_NOT_FOUND",
};

/**
 * Normalizes GET /api/auth failures from both the current backend shape
 * (`{ message }`) and the new structured shape (`{ error: { code, message } }`).
 *
 * @param {import("axios").AxiosError} error
 * @returns {{ code?: string, message?: string, isExpectedAuthFailure: boolean }}
 */
const getAuthErrorDetails = (error) => {
  const { code, message } = resolveBackendError(
    error,
    GET_AUTH_ERROR_CODES_BY_MESSAGE,
    GET_AUTH_ERROR_SHAPES,
  );
  const errorShape = GET_AUTH_ERROR_SHAPES[code];

  return {
    code,
    message,
    isExpectedAuthFailure:
      errorShape?.isExpectedAuthFailure ??
      (error?.response?.status === 401 || error?.response?.status === 404),
  };
};

/**
 * The email-link sign-in waiting in the current URL, or null. `href` is kept
 * so the link can be redeemed after the URL is cleaned.
 *
 * @returns {{ email: string, href: string } | null}
 */
const emailLinkInUrl = () => {
  const href = window.location.href;
  const email = new URLSearchParams(window.location.search).get("email");
  return email && isSignInWithEmailLink(auth, href) ? { email, href } : null;
};

// Helper function to track sign-in events
const trackSignInEvents = (user, providerName) => {
  if (!user) return;

  customEvent({
    name: `user_signed_in`,
    category: "Authorization",
    label: `Sign in with ${capitalize(providerName)} Successfully`,
    data: {
      user_id: user._id,
      user_email: user.email,
      user_handle: user.handle ?? "no_handle",
      provider: providerName,
    },
  });

  customEvent({
    name: `user_signed_in_${providerName}`,
    category: "Authorization",
    label: `Sign in with ${capitalize(providerName)} Successfully`,
    data: {
      user_id: user._id,
      user_email: user.email,
      user_handle: user.handle ?? "no_handle",
    },
  });

  if (!user.hasValidSubscription) {
    customEvent({
      name: `user_signed_in_free`,
      category: "Authorization",
      label: `Sign in with ${capitalize(providerName)} Successfully`,
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
        provider: providerName,
      },
    });

    customEvent({
      name: `free_user_${user.addedCalendars?.length || 0}`,
      category: "Authorization",
      label: `Sign in with ${capitalize(providerName)} Successfully`,
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
        provider: providerName,
      },
    });
  }

  if (!user.hasValidSubscription && user.stripeCustomerId) {
    customEvent({
      name: `user_signed_in_stripe_customer`,
      category: "Authorization",
      label: `Sign in with ${capitalize(providerName)} Successfully`,
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
        provider: providerName,
      },
    });
  }
};

export default function useAuth({ isEmbedded }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(
    () => !!sessionStorage.getItem(SIGN_IN_REDIRECT_IN_PROGRESS_KEY)
  );
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const searchParams = new URLSearchParams(window.location.search);
  const email = searchParams.get("email");
  const hasSignedInRef = useRef({
    status: false,
    providerName: null,
  });
  const shownGetAuthErrorRef = useRef(null);
  // Code of the last GET /api/auth failure, read right after a sign-in.
  const lastGetAuthErrorCodeRef = useRef(null);
  // Email-link sign-ins wait for a click so link scanners can't spend the code.
  const [pendingEmailLink, setPendingEmailLink] = useState(emailLinkInUrl);
  // Redirect sign-in rejected for an existing account, as `{ error, at, consumed }`;
  // SignIn turns it into a prompt.
  const [redirectAccountExistsError, setRedirectAccountExistsError] = useState(null);

  async function getAuth({ silent = false } = {}) {
    try {
      const response = await axios.get(getApiRoute("auth", "GET_AUTH"), {
        withCredentials: true,
      });
      shownGetAuthErrorRef.current = null;
      lastGetAuthErrorCodeRef.current = null;
      setUser(response.data);
      return response.data;
    } catch (error) {
      const { code, message, isExpectedAuthFailure } = getAuthErrorDetails(error);
      const errorKey = code ?? `${error?.response?.status}:${message ?? "unknown"}`;
      lastGetAuthErrorCodeRef.current = errorKey;

      const hadAuthenticatedUser = Boolean(user?._id);

      // Only clear the cached user on a genuine auth rejection (invalid
      // session / user-not-found). Transient failures (network / 5xx) leave
      // the session cookie valid server-side, so preserve the user rather
      // than flashing a spurious logged-out state on a flaky connection.
      if (isExpectedAuthFailure) {
        setUser(null);
      }
      if (
        hadAuthenticatedUser &&
        !silent &&
        message &&
        shownGetAuthErrorRef.current !== errorKey
      ) {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          message,
          "error"
        );
        shownGetAuthErrorRef.current = errorKey;
      }
      if (!isExpectedAuthFailure && !isAxiosRequestAbort(error)) {
        rollbar.error("[UseAuth] GET_AUTH unexpected failure", error);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Reads the signed-in user again.
   * @param {{ silent?: boolean }} [options] - silent skips the error snackbar.
   * @returns {Promise<object|null>} The user, or null when the read failed.
   */
  async function refetchAuth(options) {
    return await getAuth(options);
  }

  useEffect(() => {
    // First check if user is already authenticated, then check for OAuth redirect results
    getAuth({ silent: true }).then(() => {
      // An email link waits for confirmEmailLinkSignIn instead; no redirect is pending.
      if (pendingEmailLink) {
        sessionStorage.removeItem(SIGN_IN_REDIRECT_IN_PROGRESS_KEY);
        setIsCheckingRedirect(false);
        return;
      }
      checkSignIn(true, null);
    });
  }, []);

  /**
   * Redeems the email sign-in link in the URL. Runs only on a user click.
   *
   * @returns {Promise<{ userData: Promise<object | null> } | undefined>}
   */
  async function confirmEmailLinkSignIn() {
    if (!pendingEmailLink) return undefined;
    try {
      return await checkSignIn(false, null, { emailLink: pendingEmailLink });
    } finally {
      setPendingEmailLink(null);
    }
  }

  /** Drops the pending email link without redeeming it. */
  function dismissEmailLink() {
    setPendingEmailLink(null);
  }

  const handleSignOut = async () => {
    try {
      await axios.post(getApiRoute("auth", "POST_SIGNOUT"), null, {
        withCredentials: true,
      });
      setUser(null);
    } catch (error) {
      console.error(error);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    const host = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
    if (host) {
      connectAuthEmulator(auth, host);
    }
  }, []);

  /**
   * Completes a Firebase sign-in (popup result, redirect, or email link) into a
   * Stanza session.
   *
   * @param {boolean} withRedirect Read the pending redirect result.
   * @param {import("firebase/auth").UserCredential | null} resultFromPopup
   * @param {{ emailLink?: { email: string, href: string } }} [options] Email link to redeem.
   * @returns {Promise<{ userData: Promise<object | null> } | undefined>} Truthy
   *   once the session is minted.
   */
  const checkSignIn = async (
    withRedirect,
    resultFromPopup,
    { emailLink = null } = {}
  ) => {
    let providerName;
    let signInSucceeded = false;

    const handleSignInFailure = (err, provider) => {
      console.log(err);
      if (provider) {
        customEvent({
          name: `user_signin_error_${provider}`,
          category: "Authorization",
          label: `Sign in with ${capitalize(provider)} Error`,
          data: {
            error: err.message || "Unknown error",
            url: window.location.href,
          },
        });
      }

      reportSignInError("checkSignIn", err, {
        provider,
        emailDomain: emailDomainOf(email),
        path: window.location.pathname,
      });

      const { message: backendMessage } = resolveBackendError(err);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        backendMessage ?? firebaseAuthErrorMessage(err),
        "error"
      );
    };

    try {
      let result = resultFromPopup;
      let actionCodeInfo = "";
      if (emailLink) {
        try {
          const oobCode = new URL(emailLink.href).searchParams.get("oobCode");
          await checkActionCode(auth, oobCode);
          result = await signInWithEmailLink(auth, emailLink.email, emailLink.href);
        } catch (err) {
          console.error(err);
          reportSignInError("signInWithEmailLink", err, {
            provider: "email",
            emailDomain: emailDomainOf(emailLink.email),
            path: new URL(emailLink.href).pathname,
          });
          customEvent({
            name: "user_signin_error_email",
            category: "Authorization",
            label: "Sign in with Email Error",
            data: {
              error: err?.code || err?.message || "Unknown error",
              url: `${window.location.origin}${window.location.pathname}`,
            },
          });
          return showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            emailLinkRedeemErrorMessage(err),
            "error"
          );
        }
      } else if (withRedirect) {
        // Skip when UpdateRelayEmail is managing its own redirect result.
        // That component sets this flag before calling signInWithRedirect so
        // we don't race to consume the credential first.
        if (sessionStorage.getItem(RELAY_REDIRECT_KEY)) return;
        const redirectProvider = sessionStorage.getItem(SIGN_IN_REDIRECT_PROVIDER_KEY);
        sessionStorage.removeItem(SIGN_IN_REDIRECT_PROVIDER_KEY);
        try {
          result = await getRedirectResult(auth);
        } catch (err) {
          // The user backed out at the provider: track it, but no report or toast.
          if (isSignInCancel(err)) {
            trackProviderSignInFailure(redirectProvider || "unknown", err);
            return;
          }
          if (err?.code === "auth/account-exists-with-different-credential") {
            setRedirectAccountExistsError({ error: err, at: Date.now(), consumed: false });
          }
          throw err;
        }
      }

      if (result) {
        let idToken;

        // Determine the provider and get token
        if (result.providerId === "google.com") {
          providerName = "google";
          idToken = await result.user.getIdToken();
        } else if (result.providerId === "apple.com") {
          providerName = "apple";
          const tokenResult = await result.user.getIdTokenResult(true);
          idToken = tokenResult.token;
        } else if (result.providerId === "microsoft.com") {
          providerName = "microsoft";
          idToken = await result.user.getIdToken();
        } else if (
          result.providerId === "email" ||
          result.providerId === null
        ) {
          // It's the email link provider
          providerName = "email";
          idToken = await result.user.getIdToken();
        }

        // If provider is recognized, continue with sign in
        if (providerName && idToken) {
          const response = await axios.post(
            getApiRoute("auth", "POST_SIGNIN"),
            { idToken, provider: providerName },
            { withCredentials: true }
          );
          if (response.status === 200) {
            signInSucceeded = true;
            hasSignedInRef.current.status = true;
            hasSignedInRef.current.providerName = providerName;
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "Signing you in...",
              "success"
            );
            const userData = getAuth().then((userData) => {
              // Clear redirect-in-progress flag here so it coincides with
              // setUser() in the same React batch — buttons stay disabled
              // through the "Signing you in..." snackbar until navigation.
              sessionStorage.removeItem(SIGN_IN_REDIRECT_IN_PROGRESS_KEY);
              setIsCheckingRedirect(false);
              if (!userData) {
                // Session minted but the account didn't load: without this the
                // user is silently left on the sign-in page.
                reportSignInError(
                  "loadSessionUser",
                  Object.assign(new Error("Session minted but the user didn't load"), {
                    code: lastGetAuthErrorCodeRef.current ?? "unknown",
                  }),
                  { provider: providerName }
                );
                showSnackBar(
                  setSnackbarOpen,
                  setSnackbarMessage,
                  setSnackbarSeverity,
                  SESSION_NOT_LOADED_MESSAGE,
                  "error"
                );
              }
              // Track events after we have complete user data without useEffect to avoid race conditions.
              if (!isEmbedded && !isEmbeddedPath(window.location) && userData) {
                trackSignInEvents(userData, providerName);
              }
              return userData;
            });
            // Resolves now, as before; `userData` settles once the Stanza user
            // loads, for callers whose events need its `_id`.
            return { userData };
          } else {
            handleSignInFailure(
              new Error(response.data.message || "Unknown error"),
              providerName
            );
          }
        } else {
          handleSignInFailure(
            new Error("Missing provider or token"),
            providerName
          );
        }
      }
    } catch (err) {
      handleSignInFailure(err, providerName);
    } finally {
      // On failure or null result, clear immediately so buttons re-enable.
      // On success, the flag is cleared inside getAuth().then() so it
      // coincides with setUser() — keeping buttons disabled through navigation.
      if (!signInSucceeded) {
        sessionStorage.removeItem(SIGN_IN_REDIRECT_IN_PROGRESS_KEY);
        setIsCheckingRedirect(false);
      }
    }
  };

  return {
    user,
    isLoading,
    isCheckingRedirect,
    handleSignOut,
    refetchAuth,
    checkSignIn,
    pendingEmailLink,
    confirmEmailLinkSignIn,
    dismissEmailLink,
    redirectAccountExistsError,
    clearRedirectAccountExistsError: () => setRedirectAccountExistsError(null),
  };
}
